import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import { Table } from '../../../../components/tables/Table';
import { DeliveryStatus } from '../../../models/enums/delivery-status';
import { ShipmentStatus } from '../../../models/enums/shipment-status';
import { ShipmentType } from '../../../models/enums/shipment-type';
import { OrderSummary } from '../../../models/shared/order-summary';
import { DeliveryShipmentAction } from '../../../models/shipments/shipment-action';
import { ProductionStatusDot } from '../ProductionStatusDot';
import { ShipmentActionCustomerDeliveryInfo } from '../ShipmentActionCustomerDeliveryInfo';
import { IStopAction, StopActionsMenu } from '../StopActionsMenu';

interface IProps {
    delivery: DeliveryShipmentAction;
    routeStatus: ShipmentStatus;
    onMarkDelivery: any;
    onDeleteDelivery: any;
    onStartEdit: any;
    onAddAddon: any;
    onAddBackorder: any;
    onStartEditAddress: any;
    entityType: ShipmentType;
    serviceCenterId: string;
}

export const ActionContentCustomerDelivery = (props: IProps) => {

    /**
     * Mark the delivery as delivered and hide
     * the dialog
     */
    const markAsDelivered = async () => {

        const result = await ResultDialog.show({
            title: 'Mark Delivered?',
            message: 'Are you sure you want to mark this stop as delivered?',
            affirmativeLabel: 'Yes',
            negativeLabel: 'No'
        });

        if (result) {
            props.onMarkDelivery(props.delivery.id);
        }

    };

    function isProductionServiceCenter(): boolean {
        return ["IN", "MAIN"].indexOf(props.serviceCenterId) > -1
    }

    function canDelete(): boolean {
        if (isProductionServiceCenter()) {
            return props.routeStatus !== ShipmentStatus.Cancelled &&
                props.routeStatus < ShipmentStatus.InProduction &&
                props.delivery.status === DeliveryStatus.Open
        }
        else {
            return props.routeStatus !== ShipmentStatus.Closed &&
                props.routeStatus !== ShipmentStatus.Cancelled &&
                props.delivery.status === DeliveryStatus.Open
        }
    }

    /**
     * Confirm and delete the delivery. Done through the result dialog, rather than a
     * confirmation trigger, so that the dialog is not lost when the actions menu closes.
     */
    const confirmDelete = async () => {

        const result = await ResultDialog.show({
            title: 'Delete Customer Delivery?',
            message: 'Are you sure you want to delete this delivery?',
            affirmativeLabel: 'Yes',
            negativeLabel: 'No'
        });

        if (result) {
            props.onDeleteDelivery(props.delivery.id);
        }

    };

    /**
     * Every action the stop allows right now, for the actions menu, which leaves out
     * those the user is not permitted to take.
     */
    const getActions = (): IStopAction[] => {

        const isOpen = props.delivery.status === DeliveryStatus.Open;
        const canEditInProduction = isProductionServiceCenter() && isOpen
            && props.routeStatus >= ShipmentStatus.InProduction && props.routeStatus <= ShipmentStatus.InTransit;
        const editPermissions = [Permissions.ShippingTrucksEdit, Permissions.ShippingContainersEdit];

        const available: IStopAction[] = [];

        if (isOpen) {
            available.push({
                id: 'stop-action-edit', text: 'Edit', icon: 'la-pencil',
                requiredPermissions: editPermissions,
                onClick: () => props.onStartEdit(props.delivery.id)
            });
        }

        if (canEditInProduction) {
            available.push({
                id: 'stop-action-addon', text: 'Add-on', icon: 'la-plus-circle',
                requiredPermissions: editPermissions,
                onClick: () => props.onAddAddon(props.delivery.id)
            });
            available.push({
                id: 'stop-action-backorder', text: 'Backorder', icon: 'la-undo',
                requiredPermissions: [Permissions.OrderingBackorder, ...editPermissions],
                onClick: () => props.onAddBackorder(props.delivery.id)
            });
        }

        if (isOpen) {
            available.push({
                id: 'stop-action-edit-address', text: 'Edit Address', icon: 'la-map-marker',
                requiredPermissions: editPermissions,
                onClick: () => props.onStartEditAddress(props.delivery.id)
            });
        }

        if (props.routeStatus === ShipmentStatus.InTransit && isOpen) {
            available.push({
                id: 'stop-action-deliver', text: 'Deliver', icon: 'la-check-circle',
                requiredPermissions: editPermissions,
                onClick: markAsDelivered
            });
        }

        if (canDelete()) {
            available.push({
                id: 'stop-action-delete', text: 'Delete', icon: 'la-trash', classes: 'delete-action',
                requiredPermissions: editPermissions,
                onClick: confirmDelete
            });
        }

        return available;
    };

    return <div>
        <div className="row">
            <div className="col-md-12">
                <div className="stop-panel-heading">
                    <h1 className="stop-panel-title">Customer Delivery</h1>
                    <StopActionsMenu actions={getActions()} />
                </div>
            </div>
        </div>
        <div className="row">
            <div className="col-md-12">
                <ShipmentActionCustomerDeliveryInfo
                    changeAddress={undefined}
                    stop={props.delivery}
                    entityType={props.entityType}
                    includeFreight={true}
                />
            </div>
        </div>

        <div className="row">
            <div className="col-md-12">
                <Table
                    header={[
                        'Order ID',
                        'Description',
                        'Job Name',
                        'Quantity',
                        'Production',
                    ]}
                    headerClasses={['', '', '', 'table-cell-center', 'table-cell-center']}
                    nextClick={undefined}
                    noResultsMessage="No Orders on this Stop">
                    {
                        props.delivery.orders.map((tmpObj: OrderSummary) =>
                            <tr key={tmpObj.id}>
                                <td>{tmpObj.id}</td>
                                <td>{tmpObj.description}</td>
                                <td>{tmpObj.jobName}</td>
                                <td className="table-cell-center">{tmpObj.quantity}</td>
                                <td className="table-cell-center">
                                    <ProductionStatusDot status={tmpObj.productionStatus} />
                                </td>
                            </tr>
                        )
                    }
                </Table>
            </div>
        </div>

    </div>;
};
